import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Search,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ExternalLink,
  Sparkles,
  Lock,
  FileWarning,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPO_LABEL = {
  home: "Home",
  privacy: "Política de privacidad",
  cookies: "Política de cookies",
  checkout: "Checkout / carrito",
  contact: "Contacto",
  forms: "Formularios",
  other: "Página interna",
};

const RIESGO_STYLES = {
  alto: "bg-red-50 text-red-800 border-red-200",
  medio: "bg-amber-50 text-amber-800 border-amber-200",
  bajo: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

export default function DeepScanCard({ isPro, onCheckout }) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleScan = async () => {
    if (!url.trim()) {
      toast.error("Ingresa una URL para escanear");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/scan/deep`,
        { url: url.trim() },
        { withCredentials: true },
      );
      setResult(data);
      toast.success(`Deep scan completo · ${data.paginas_analizadas?.length || 0} páginas analizadas`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "No pudimos completar el deep scan";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!isPro) {
    return (
      <section
        data-testid="deep-scan-card-gated"
        className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl p-6 sm:p-8 border border-slate-800"
      >
        <div className="flex items-start gap-4">
          <div className="bg-white/10 rounded-xl p-3 shrink-0">
            <Search className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider bg-amber-400 text-slate-900 px-2 py-0.5 rounded mb-2">
              <Lock className="w-3 h-3" /> Pro
            </div>
            <h3 className="text-xl font-bold">Deep scan — escaneo profundo</h3>
            <p className="mt-2 text-sm text-slate-300 leading-relaxed">
              Analiza hasta 5 páginas internas de tu sitio (home, privacidad, cookies, contacto, checkout) y detecta
              pixels de tracking que solo se cargan en páginas específicas, como Meta Pixel en checkout o Google
              Analytics en formularios.
            </p>
            <button
              type="button"
              onClick={() => onCheckout?.()}
              data-testid="deep-scan-upgrade-cta"
              className="mt-4 inline-flex items-center gap-2 bg-white text-slate-900 text-sm font-semibold px-4 py-2 rounded-full hover:bg-slate-100 transition-colors"
            >
              Activar Pro · $14.990 CLP · pago único
              <Sparkles className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      data-testid="deep-scan-card"
      className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8"
    >
      <div className="flex items-start gap-4 mb-6">
        <div className="bg-blue-50 text-blue-600 rounded-xl p-3 shrink-0">
          <Search className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-xl font-bold text-slate-900">Deep scan Pro</h3>
          <p className="mt-1 text-sm text-slate-600 leading-relaxed">
            Escanea hasta 5 páginas internas de tu sitio para detectar pixels que solo cargan en checkout, formularios o
            páginas internas. Útil cuando el scan simple no captura tracking de terceros.
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://tu-sitio.cl"
          disabled={loading}
          data-testid="deep-scan-url-input"
          className="flex-1 px-4 py-3 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
          onKeyDown={(e) => e.key === "Enter" && handleScan()}
        />
        <button
          onClick={handleScan}
          disabled={loading || !url.trim()}
          data-testid="deep-scan-submit"
          className="inline-flex items-center justify-center gap-2 bg-slate-900 text-white text-sm font-semibold px-6 py-3 rounded-xl hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Escaneando…
            </>
          ) : (
            <>
              <Search className="w-4 h-4" />
              Lanzar deep scan
            </>
          )}
        </button>
      </div>

      {loading && (
        <p className="mt-3 text-xs text-slate-500" data-testid="deep-scan-progress">
          Analizando páginas en paralelo. Esto puede tomar 20-40 segundos…
        </p>
      )}

      {result && <DeepScanResult result={result} />}
    </section>
  );
}

function DeepScanResult({ result }) {
  const okPages = result.paginas_analizadas?.filter((p) => p.ok) || [];
  const failedPages = result.paginas_analizadas?.filter((p) => !p.ok) || [];
  const trackers = result.trackers_consolidados || [];
  const scoreColor =
    result.score >= 80 ? "text-emerald-600" : result.score >= 50 ? "text-amber-600" : "text-red-600";

  return (
    <div className="mt-8 space-y-6" data-testid="deep-scan-result">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-6 border-b border-slate-200">
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Resultado consolidado</p>
          <p className="mt-1 text-base text-slate-900 max-w-2xl leading-relaxed">{result.resumen}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Score</p>
          <p className={`text-4xl font-bold tabular-nums ${scoreColor}`} data-testid="deep-scan-score">
            {result.score}
            <span className="text-base text-slate-400">/100</span>
          </p>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          Páginas analizadas ({okPages.length})
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {okPages.map((p, i) => (
            <div
              key={`ok-${i}`}
              className="border border-slate-200 rounded-xl p-4 bg-slate-50/50"
              data-testid={`deep-scan-page-${p.tipo}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <p className="text-xs font-bold text-slate-900 uppercase tracking-wide">
                  {TIPO_LABEL[p.tipo] || p.tipo}
                </p>
                <a
                  href={p.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-slate-400 hover:text-slate-700 shrink-0"
                  aria-label="Abrir página"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
              <p className="text-xs text-slate-600 truncate mb-3">{p.url}</p>
              <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                <span className={`px-2 py-0.5 rounded-full font-medium ${p.usa_https ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                  {p.usa_https ? "HTTPS" : "Sin HTTPS"}
                </span>
                {p.tiene_banner_cookies && (
                  <span className="px-2 py-0.5 rounded-full font-medium bg-blue-50 text-blue-700">Cookies</span>
                )}
                {p.formularios_count > 0 && (
                  <span className="px-2 py-0.5 rounded-full font-medium bg-slate-200 text-slate-700">
                    {p.formularios_count} form{p.formularios_count > 1 ? "s" : ""}
                  </span>
                )}
                {p.scripts_terceros?.length > 0 && (
                  <span className="px-2 py-0.5 rounded-full font-medium bg-amber-50 text-amber-800">
                    {p.scripts_terceros.length} tracker{p.scripts_terceros.length > 1 ? "s" : ""}
                  </span>
                )}
              </div>
              {p.scripts_terceros?.length > 0 && (
                <p className="mt-2 text-[11px] text-slate-500 leading-relaxed">
                  {p.scripts_terceros.join(" · ")}
                </p>
              )}
            </div>
          ))}
        </div>
        {failedPages.length > 0 && (
          <div className="mt-3 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <XCircle className="w-3.5 h-3.5 text-slate-400" />
              {failedPages.length} página(s) no se pudieron analizar:{" "}
              {failedPages.map((p) => `${TIPO_LABEL[p.tipo] || p.tipo} (${p.error || "error"})`).join(", ")}
            </span>
          </div>
        )}
      </div>

      {trackers.length > 0 && (
        <div>
          <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            Trackers detectados ({trackers.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {trackers.map((t) => (
              <span
                key={t}
                data-testid={`deep-scan-tracker-${t.toLowerCase().replace(/\s+/g, "-")}`}
                className="inline-flex items-center gap-1.5 text-xs font-medium bg-amber-50 text-amber-900 border border-amber-200 px-2.5 py-1 rounded-full"
              >
                <AlertTriangle className="w-3 h-3" />
                {t}
              </span>
            ))}
          </div>
          <p className="mt-2 text-xs text-slate-500 leading-relaxed">
            Cada uno de estos terceros debe declararse en tu política de privacidad y bloquearse hasta obtener
            consentimiento del usuario, según la Ley 21.719.
          </p>
        </div>
      )}

      {result.riesgos_por_pagina?.length > 0 && (
        <div>
          <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
            <FileWarning className="w-4 h-4 text-blue-600" />
            Recomendaciones por página ({result.riesgos_por_pagina.reduce((acc, p) => acc + (p.riesgos?.length || 0), 0)})
          </h4>
          <div className="space-y-4">
            {result.riesgos_por_pagina.map((page, idx) => (
              <div
                key={`pg-risk-${idx}`}
                data-testid={`deep-scan-page-risk-${page.tipo}`}
                className="border border-slate-200 rounded-xl overflow-hidden"
              >
                <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">{page.tipo_label}</p>
                    <p className="text-xs text-slate-900 font-medium truncate">{page.url}</p>
                  </div>
                  <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold bg-blue-100 text-blue-800 border border-blue-200 px-2 py-0.5 rounded-full">
                    {page.riesgos.length} hallazgo{page.riesgos.length > 1 ? "s" : ""}
                  </span>
                </div>
                <div className="divide-y divide-slate-100">
                  {page.riesgos.map((r, ri) => (
                    <div key={ri} className="p-4">
                      <div className="flex items-start gap-3">
                        <span
                          className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider border px-2 py-0.5 rounded shrink-0 ${RIESGO_STYLES[r.riesgo] || RIESGO_STYLES.bajo}`}
                        >
                          {r.riesgo}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-900 text-sm">{r.titulo}</p>
                          {r.obligacion_legal && (
                            <p className="mt-1 text-[11px] text-slate-500 italic">{r.obligacion_legal}</p>
                          )}
                          {r.que_encontramos && (
                            <p className="mt-2 text-xs text-slate-700 leading-relaxed">
                              <span className="font-semibold text-slate-900">Qué encontramos: </span>
                              {r.que_encontramos}
                            </p>
                          )}
                          {r.recomendacion && (
                            <p className="mt-2 text-xs text-slate-700 leading-relaxed">
                              <span className="font-semibold text-emerald-700">Acción: </span>
                              {r.recomendacion}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {result.hallazgos?.length > 0 && (
        <div>
          <h4 className="text-sm font-bold text-slate-900 mb-3">Hallazgos consolidados ({result.hallazgos.length})</h4>
          <div className="space-y-3">
            {result.hallazgos.map((h, i) => (
              <div
                key={i}
                className="border border-slate-200 rounded-xl p-4"
                data-testid={`deep-scan-hallazgo-${i}`}
              >
                <div className="flex items-start gap-3">
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider border px-2 py-0.5 rounded shrink-0 ${RIESGO_STYLES[h.riesgo] || RIESGO_STYLES.bajo}`}
                  >
                    {h.riesgo}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 text-sm">{h.titulo}</p>
                    {h.recomendacion && (
                      <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">{h.recomendacion}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
